import { apiFetch } from '@/lib/api-fetch.js';

/**
 * The file viewer's Assistant thread (`cti-conversation-service`). A file has one edit chat per
 * workspace: the chat the agent made it in, while that chat's audience matches the file's, or else a
 * dedicated thread created on the first send. The server decides which on every request; this module
 * only asks and creates.
 *
 * REST is snake_case: the file travels as `{ source, artifact_id }`, `{ source, drive_id, item_id }`
 * or `{ source, scope_id, item_id }`, in the query of the lookup and in the body of the create.
 */

/** One file the viewer can have open, addressed the way its source addresses it. */
export type FileReference =
  | { source: 'artifact'; artifactId: string }
  | { source: 'sharepoint'; driveId: string; itemId: string }
  | { source: 'onedrive'; scopeId: string; itemId: string };

/** The same file on the wire. */
export type FileReferenceWire =
  | { source: 'artifact'; artifact_id: string }
  | { source: 'sharepoint'; drive_id: string; item_id: string }
  | { source: 'onedrive'; scope_id: string; item_id: string };

/** How the file's edit chat was found: the chat it was made in, its own thread, or none yet. */
export type FileThreadMode = 'origin' | 'dedicated' | 'none';

export interface FileThreadState {
  mode: FileThreadMode;
  conversationId: string | null;
  /** False only for an origin chat the caller may not read, such as an author's private chat. */
  readable: boolean;
}

interface FileThreadWire {
  mode?: string;
  conversation_id?: string | null;
  readable?: boolean;
}

/** The file in its snake_case wire form. */
export function fileReferenceToWire(file: FileReference): FileReferenceWire {
  if (file.source === 'artifact') {
    return { source: 'artifact', artifact_id: file.artifactId };
  }
  if (file.source === 'sharepoint') {
    return {
      source: 'sharepoint',
      drive_id: file.driveId,
      item_id: file.itemId,
    };
  }
  return { source: 'onedrive', scope_id: file.scopeId, item_id: file.itemId };
}

/** One string per file, for cache keys and for keying a panel. */
export function fileReferenceKey(file: FileReference): string {
  return Object.values(fileReferenceToWire(file)).join(':');
}

/** Whether a stored file reference names the same file. A malformed one names nothing. */
export function sameFile(wire: unknown, file: FileReference): boolean {
  if (typeof wire !== 'object' || wire === null) return false;
  const expected = fileReferenceToWire(file) as Record<string, string>;
  const actual = wire as Record<string, unknown>;
  return Object.keys(expected).every((key) => actual[key] === expected[key]);
}

function stateFromWire(wire: FileThreadWire): FileThreadState {
  const mode: FileThreadMode =
    wire.mode === 'origin' || wire.mode === 'dedicated' ? wire.mode : 'none';
  return {
    mode,
    conversationId: wire.conversation_id ?? null,
    readable: wire.readable !== false,
  };
}

/**
 * Answers that mean "no Assistant for this file here": a service without the route (400, 404, 405)
 * and a file the caller may not read (403, 404). The viewer then shows the file alone.
 */
const UNSUPPORTED_STATUSES = new Set([400, 403, 404, 405]);

/** Shown when the service cannot answer, so the file's chat cannot be found. */
const UNAVAILABLE_MESSAGE =
  'The assistant is unavailable right now. Try again in a moment.';

function failure(response: Response, action: string): Error {
  if (response.status === 503) return new Error(UNAVAILABLE_MESSAGE);
  return new Error(
    `Could not ${action} the assistant chat (${response.status})`,
  );
}

export const fileChatKeys = {
  thread: (workspaceId: string, file: FileReference) =>
    ['file-chat', workspaceId, fileReferenceKey(file)] as const,
};

/**
 * The file's edit chat, or null when this service has no file chat or the caller cannot read the
 * file. Nothing is created by asking.
 */
export async function fetchFileThread(
  workspaceId: string,
  file: FileReference,
): Promise<FileThreadState | null> {
  const query = new URLSearchParams({
    workspace_id: workspaceId,
    ...fileReferenceToWire(file),
  });
  const response = await apiFetch(
    `/v1/conversations/file-thread?${query.toString()}`,
  );
  if (UNSUPPORTED_STATUSES.has(response.status)) return null;
  if (!response.ok) throw failure(response, 'load');
  return stateFromWire((await response.json()) as FileThreadWire);
}

/**
 * Get or create the file's edit chat. The server answers with the existing one when there is one, so
 * two people sending their first message at once land in the same conversation.
 */
export async function createFileThread(input: {
  workspaceId: string;
  file: FileReference;
  title: string;
}): Promise<FileThreadState> {
  const response = await apiFetch('/v1/conversations/file-thread', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspace_id: input.workspaceId,
      file: fileReferenceToWire(input.file),
      title: input.title,
    }),
  });
  if (!response.ok) throw failure(response, 'start');
  return stateFromWire((await response.json()) as FileThreadWire);
}

/** Raised when an assistant edit cannot be undone; the message is the server's own sentence. */
export class FileEditUndoError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'FileEditUndoError';
  }
}

async function undoErrorMessage(response: Response): Promise<string> {
  const body = (await response.json().catch(() => null)) as {
    message?: unknown;
  } | null;
  if (typeof body?.message === 'string' && body.message.trim()) {
    return body.message;
  }
  return `Could not undo the assistant's edit (${response.status})`;
}

/**
 * Reverts the file change one assistant message made. The server refuses with 409 when the file
 * changed after that edit, or when it was undone already.
 */
export async function undoFileEdit(
  conversationId: string,
  messageId: string,
): Promise<void> {
  const response = await apiFetch(
    `/v1/conversations/${encodeURIComponent(conversationId)}/messages/${encodeURIComponent(messageId)}/file-edit/undo`,
    { method: 'POST' },
  );
  if (!response.ok) {
    throw new FileEditUndoError(
      response.status,
      await undoErrorMessage(response),
    );
  }
}
